import { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
} from "react-router-dom";
import type {
  LeadForm,
  LeadFormField,
  LeadFormKind,
} from "@sales-ops-amplify/shared";
import { WORKFLOW_TEMPLATES } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  useCreateLeadForm,
  useDeleteLeadForm,
  useFormEmbed,
  useFormSubmissions,
  useLeadForm,
  useLeadForms,
  usePublishLeadForm,
  useUpdateLeadForm,
} from "@/hooks/useForms";

const TABS = [
  { to: "/forms", label: "All forms", end: true },
  { to: "/forms/enrichment", label: "Form enrichment", end: true },
];

function formatWhen(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function FormsLayout() {
  return (
    <div className="space-y-5 pb-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Forms</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Build website forms or enrich existing ones — capture leads and
            trigger workflows.
          </p>
        </div>
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              cn(
                "px-3 py-2 text-sm",
                isActive
                  ? "border-b-2 border-brand font-medium text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}

export function FormsListPage() {
  const navigate = useNavigate();
  const list = useLeadForms();
  const create = useCreateLeadForm();
  const forms = (list.data?.forms ?? []).filter((f) => f.kind === "website");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
          disabled={create.isPending}
          onClick={async () => {
            const { form } = await create.mutateAsync({
              kind: "website",
              name: "Website form",
            });
            navigate(`/forms/${form.formId}/edit`);
          }}
        >
          Create website form
        </button>
        <Link
          to="/forms/enrichment"
          className="rounded-md border border-border px-3 py-2 text-sm"
        >
          Create form enrichment
        </Link>
      </div>

      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading forms…</p>
      ) : forms.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">
            No website forms yet. Create one to publish an embeddable lead form.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((f) => (
            <FormCard key={f.formId} form={f} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FormsEnrichmentListPage() {
  const navigate = useNavigate();
  const list = useLeadForms();
  const create = useCreateLeadForm();
  const forms = (list.data?.forms ?? []).filter((f) => f.kind === "enrichment");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Bind enrichment to an existing HTML form on your site — autofill known
          contact fields from your CRM after email entry.
        </p>
        <button
          type="button"
          className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground"
          disabled={create.isPending}
          onClick={async () => {
            const { form } = await create.mutateAsync({
              kind: "enrichment",
              name: "Form enrichment",
            });
            navigate(`/forms/${form.formId}/edit`);
          }}
        >
          Create enrichment
        </button>
      </div>
      {list.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : forms.length === 0 ? (
        <p className="text-sm text-muted-foreground">No enrichment configs yet.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {forms.map((f) => (
            <FormCard key={f.formId} form={f} />
          ))}
        </div>
      )}
    </div>
  );
}

function FormCard({ form }: { form: LeadForm }) {
  return (
    <Link
      to={`/forms/${form.formId}`}
      className="rounded-lg border border-border bg-card p-4 hover:border-brand/40"
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {form.kind === "enrichment" ? "Enrichment" : "Website"} · {form.status}
      </p>
      <p className="mt-1 font-medium text-foreground">{form.name}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {form.fields.length} fields · updated {formatWhen(form.updatedAt)}
      </p>
    </Link>
  );
}

const MAP_OPTIONS = [
  "lead.name",
  "lead.email",
  "lead.phone",
  "lead.title",
  "company.name",
  "company.domain",
] as const;

function FieldEditor({
  fields,
  kind,
  onChange,
}: {
  fields: LeadFormField[];
  kind: LeadFormKind;
  onChange: (fields: LeadFormField[]) => void;
}) {
  return (
    <div className="space-y-3">
      {fields.map((f, idx) => (
        <div
          key={f.id}
          className="rounded-lg border border-border bg-card p-3 space-y-2"
        >
          <div className="flex flex-wrap gap-2">
            <input
              className="flex-1 min-w-[140px] rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={f.label}
              onChange={(e) => {
                const next = [...fields];
                next[idx] = { ...f, label: e.target.value };
                onChange(next);
              }}
            />
            <select
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={f.type}
              onChange={(e) => {
                const next = [...fields];
                next[idx] = {
                  ...f,
                  type: e.target.value as LeadFormField["type"],
                };
                onChange(next);
              }}
            >
              {[
                "email",
                "name",
                "phone",
                "text",
                "textarea",
                "select",
                "checkbox",
                "hidden",
              ].map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={f.mapTo}
              onChange={(e) => {
                const next = [...fields];
                next[idx] = { ...f, mapTo: e.target.value };
                onChange(next);
              }}
            >
              {MAP_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          {kind === "enrichment" ? (
            <label className="block text-xs text-muted-foreground">
              HTML name / id
              <input
                className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
                value={f.htmlName ?? ""}
                onChange={(e) => {
                  const next = [...fields];
                  next[idx] = { ...f, htmlName: e.target.value };
                  onChange(next);
                }}
              />
            </label>
          ) : null}
          <div className="flex flex-wrap gap-3 text-xs">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!f.required}
                onChange={(e) => {
                  const next = [...fields];
                  next[idx] = { ...f, required: e.target.checked };
                  onChange(next);
                }}
              />
              Required
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={f.autoFill !== false}
                onChange={(e) => {
                  const next = [...fields];
                  next[idx] = { ...f, autoFill: e.target.checked };
                  onChange(next);
                }}
              />
              Auto-fill
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={!!f.hideKnown}
                onChange={(e) => {
                  const next = [...fields];
                  next[idx] = { ...f, hideKnown: e.target.checked };
                  onChange(next);
                }}
              />
              Hide when known
            </label>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onChange(fields.filter((_, i) => i !== idx))}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        className="text-sm text-brand hover:underline"
        onClick={() =>
          onChange([
            ...fields,
            {
              id: crypto.randomUUID(),
              type: "text",
              label: "Custom field",
              mapTo: "lead.title",
              htmlName: "custom_field",
              autoFill: true,
            },
          ])
        }
      >
        Add field
      </button>
    </div>
  );
}

export function FormsEditPage() {
  const { formId = "" } = useParams();
  const navigate = useNavigate();
  const formQ = useLeadForm(formId);
  const update = useUpdateLeadForm();
  const publish = usePublishLeadForm();
  const del = useDeleteLeadForm();
  const form = formQ.data?.form;
  const [domainDraft, setDomainDraft] = useState("");

  if (formQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading form…</p>;
  }
  if (!form) {
    return <p className="text-sm text-destructive">Form not found.</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={form.kind === "enrichment" ? "/forms/enrichment" : "/forms"}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Forms
          </Link>
          <input
            className="mt-1 block w-full max-w-md border-0 bg-transparent text-xl font-semibold text-foreground outline-none"
            value={form.name}
            onChange={(e) =>
              update.mutate({
                formId,
                patch: { name: e.target.value },
              })
            }
          />
          <p className="text-xs text-muted-foreground">
            {form.kind} · {form.status}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
            disabled={publish.isPending}
            onClick={async () => {
              const domains = domainDraft.trim()
                ? [
                    ...new Set([
                      ...form.domains,
                      domainDraft.trim().replace(/\/$/, ""),
                    ]),
                  ]
                : form.domains;
              await publish.mutateAsync({ formId, domains });
              navigate(`/forms/${formId}`);
            }}
          >
            Publish
          </button>
          <button
            type="button"
            className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive"
            onClick={async () => {
              await del.mutateAsync(formId);
              navigate(form.kind === "enrichment" ? "/forms/enrichment" : "/forms");
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <label className="block text-xs text-muted-foreground">
        Publish domain (https://…)
        <input
          className="mt-1 block w-full max-w-md rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
          placeholder="https://acme.com"
          value={domainDraft || form.domains[0] || ""}
          onChange={(e) => setDomainDraft(e.target.value)}
        />
      </label>

      <section>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Fields
        </h2>
        <FieldEditor
          fields={form.fields}
          kind={form.kind}
          onChange={(fields) => update.mutate({ formId, patch: { fields } })}
        />
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Enrichment
        </h2>
        {(
          [
            ["autoFill", "Auto-fill known fields"],
            ["hideKnown", "Hide known fields"],
            ["phoneEnrich", "Phone number enrichment (best-effort from CRM)"],
            ["companyEmailOnly", "Enrich only company emails"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enrichmentSettings[key]}
              onChange={(e) =>
                update.mutate({
                  formId,
                  patch: {
                    enrichmentSettings: {
                      ...form.enrichmentSettings,
                      [key]: e.target.checked,
                    },
                  },
                })
              }
            />
            {label}
          </label>
        ))}
      </section>

      {form.kind === "website" ? (
        <section className="rounded-lg border border-border bg-card p-4 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Design & success
          </h2>
          <label className="block text-xs text-muted-foreground">
            Button label
            <input
              className="mt-1 block w-48 rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.design.buttonLabel ?? "Submit"}
              onChange={(e) =>
                update.mutate({
                  formId,
                  patch: {
                    design: { ...form.design, buttonLabel: e.target.value },
                  },
                })
              }
            />
          </label>
          <label className="block text-xs text-muted-foreground">
            Primary color
            <input
              type="color"
              className="mt-1 block h-9 w-16 rounded border border-border"
              value={form.design.primaryColor ?? "#2563eb"}
              onChange={(e) =>
                update.mutate({
                  formId,
                  patch: {
                    design: { ...form.design, primaryColor: e.target.value },
                  },
                })
              }
            />
          </label>
          <label className="block text-xs text-muted-foreground">
            Success message
            <input
              className="mt-1 block w-full max-w-lg rounded-md border border-border bg-background px-2 py-1.5 text-sm text-foreground"
              value={form.successMessage ?? ""}
              onChange={(e) =>
                update.mutate({
                  formId,
                  patch: { successMessage: e.target.value },
                })
              }
            />
          </label>
        </section>
      ) : null}
    </div>
  );
}

export function FormsDetailPage() {
  const { formId = "" } = useParams();
  const [tab, setTab] = useState<"submissions" | "embed" | "settings">(
    "submissions",
  );
  const formQ = useLeadForm(formId);
  const subs = useFormSubmissions(formId);
  const embed = useFormEmbed(formId);
  const form = formQ.data?.form;
  const templates = WORKFLOW_TEMPLATES.filter(
    (t) =>
      t.category === "Inbound" && t.draft.trigger?.type === "form_submitted",
  );

  if (formQ.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (!form) {
    return <p className="text-sm text-destructive">Form not found.</p>;
  }

  const snippet =
    form.kind === "enrichment"
      ? embed.data?.enrichment
      : embed.data?.hosted;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={form.kind === "enrichment" ? "/forms/enrichment" : "/forms"}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Forms
          </Link>
          <h2 className="mt-1 text-xl font-semibold">{form.name}</h2>
          <p className="text-xs text-muted-foreground">
            {form.kind} · {form.status}
            {embed.data?.publicUrl ? (
              <>
                {" "}
                ·{" "}
                <a
                  href={embed.data.publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  Preview
                </a>
              </>
            ) : null}
          </p>
        </div>
        <Link
          to={`/forms/${formId}/edit`}
          className="rounded-md border border-border px-3 py-1.5 text-sm"
        >
          Edit
        </Link>
      </div>

      <div className="flex gap-1 border-b border-border">
        {(
          [
            ["submissions", "Submissions"],
            ["embed", "Embed"],
            ["settings", "Settings"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              "px-3 py-2 text-sm",
              tab === id
                ? "border-b-2 border-brand font-medium"
                : "text-muted-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "submissions" ? (
        <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase text-muted-foreground">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {(subs.data?.submissions ?? []).map((s) => (
                <tr key={s.submissionId} className="border-b border-border/60">
                  <td className="px-3 py-2 text-muted-foreground">
                    {formatWhen(s.occurredAt)}
                  </td>
                  <td className="px-3 py-2">
                    {s.leadId ? (
                      <Link
                        to={`/user/leads?q=${encodeURIComponent(s.leadId)}`}
                        className="text-brand hover:underline"
                      >
                        {s.values["lead.name"] ||
                          s.values.name ||
                          s.leadId.slice(0, 8)}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {s.values["lead.email"] || s.values.email || "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{s.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(subs.data?.submissions ?? []).length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              No submissions yet. Publish and embed the form to collect leads.
            </p>
          ) : null}
        </div>
      ) : null}

      {tab === "embed" ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {form.kind === "enrichment"
              ? "Paste this on pages that contain your existing HTML form."
              : "Paste this where you want the hosted form to appear."}
          </p>
          <textarea
            readOnly
            rows={6}
            className="w-full rounded-md border border-border bg-muted/20 px-3 py-2 font-mono text-xs"
            value={snippet ?? "Loading…"}
          />
          <button
            type="button"
            className="text-sm text-brand hover:underline"
            disabled={!snippet}
            onClick={() => snippet && navigator.clipboard.writeText(snippet)}
          >
            Copy snippet
          </button>
        </div>
      ) : null}

      {tab === "settings" ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-sm text-muted-foreground">
              Domains:{" "}
              {form.domains.length ? form.domains.join(", ") : "None set"}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Public key: <code className="text-xs">{form.publicKey}</code>
            </p>
          </div>
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Workflows
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {templates.map((t) => (
                <Link
                  key={t.id}
                  to={`/workflows?template=${encodeURIComponent(t.id)}`}
                  className="rounded-lg border border-border bg-card p-3 text-sm hover:border-brand/40"
                >
                  <p className="font-medium">{t.name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Public hosted form preview route */
export function PublicFormPage() {
  const { publicKey = "" } = useParams();
  if (!publicKey) return <Navigate to="/forms" replace />;
  return (
    <div className="flex min-h-screen items-start justify-center bg-zinc-50 px-4 py-16">
      <div id={`zso-form-${publicKey}`} />
      <PublicFormBoot publicKey={publicKey} />
    </div>
  );
}

function PublicFormBoot({ publicKey }: { publicKey: string }) {
  useEffect(() => {
    const id = `zso-script-${publicKey}`;
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.src = `/api/public/f/${publicKey}/script.js?mode=hosted`;
    document.body.appendChild(s);
    return () => {
      s.remove();
    };
  }, [publicKey]);
  return null;
}
